/* eslint-disable import/no-duplicates, import/order, no-unused-vars */
import { useMemo, useRef } from 'react';
import mockApiProvider from './mockApiProvider';
import useUploadSession from '../studioUploads/useUploadSession';
import { getUploadSource } from '../studioUploads/session';
import getInitStory from './getStoryResponse';
import { PAGE_WIDTH } from '@web-stories-wp/units';
import {
  getImageDimensions,
  getVideoDimensions,
  getVideoLength,
  getVideoTrimRange,
} from '@web-stories-wp/media';
import useCmsApi from './useCmsApi';
import { useAuthorizationManager } from '@hooks/authorization';
import { v4 as uuidv4 } from 'uuid';
import {
  AssetTypes,
  isAnimatedThumbnailAssetType,
} from '../studioContext/CMSParams';
import {
  EntityType,
  EventTargetElement,
  trackAppInsightsError,
  trackAppInsightsEvent,
} from '../../initAppInsights';
import { getIncompleteVideoError } from '../../utils/upload/videoCompleteness';
import { wasPageChanged } from '../../utils/wasPageChanged';
import { pageToCanvasPage } from '../../utils/pageToCanvasPage';
import { PageType } from '@src/enums/pageType';
import { clearPersistedMediaLibraryAssets } from '../studioContext/mediaLibraryAssets';

const LIST_PAGE_SIZE = 100;
const INVALID_PAGED_LIST_RESPONSE = 'Unable to load the complete Studio list.';
const DEFAULT_MEDIA_WIDTH = 1080;
const DEFAULT_MEDIA_HEIGHT = 1920;
const VIDEO_MIME_PREFIX = 'video/';
const VIDEO_URL_EXTENSIONS_REGEX = /\.(mp4|mov|m4v|webm|m3u8)$/i;
const ANIMATED_THUMBNAIL_DURATION_SECONDS = 3;

const hasRenderableLottieElement = (page) =>
  page.elements?.some(
    (elem) =>
      elem.type === 'lottie' &&
      elem.isHidden !== true &&
      elem.definition &&
      typeof elem.definition === 'object' &&
      !Array.isArray(elem.definition)
  ) ?? false;

const hasVideoRenderingElement = (page) =>
  page.elements?.some((elem) => elem.type === 'video') ||
  hasRenderableLottieElement(page);

const normalizeMediaUrl = (url) =>
  typeof url === 'string' ? url.split(/[?#]/, 1)[0] : '';

const findAnimatedThumbnailSourceElements = (page, source) => {
  const videoElements = (page.elements || []).filter(
    (element) => element.type === 'video' && element.resource
  );
  const sourceUrl = normalizeMediaUrl(source?.url);

  return videoElements.filter(
    (element) =>
      (Boolean(source?.sourceId) && element.resource.id === source.sourceId) ||
      (Boolean(sourceUrl) &&
        normalizeMediaUrl(element.resource.src) === sourceUrl)
  );
};

const prepareAnimatedThumbnailStudioDefinition = (studioDefinition, source) => {
  const definition = JSON.parse(studioDefinition);
  const page = definition.pages?.[0];
  const sourceElements = page
    ? findAnimatedThumbnailSourceElements(page, source)
    : [];

  if (sourceElements.length === 0) {
    throw new Error(
      'The animated-thumbnail source video is missing from the page.'
    );
  }

  const sourceElementSet = new Set(sourceElements);
  page.elements = (page.elements || []).map((element) =>
    sourceElementSet.has(element)
      ? {
          ...element,
          resource: {
            ...element.resource,
            ...(source?.sourceId ? { id: source.sourceId } : {}),
            ...(source?.url ? { src: source.url } : {}),
          },
        }
      : element
  );

  return {
    definition: JSON.stringify(definition),
    studioPageId: page?.id || null,
  };
};

const throwInvalidPagedListResponse = () => {
  throw new Error(INVALID_PAGED_LIST_RESPONSE);
};

const throwIfAborted = (signal) => {
  if (!signal?.aborted) {
    return;
  }

  const error = new Error('The operation was aborted.');
  error.name = 'AbortError';
  throw error;
};

async function getDataFromList(
  getList,
  { assetType, signal, requestParams = {} } = {}
) {
  const items = [];
  const seenIds = new Set();
  let hasTotalCount;
  let totalCount;

  while (true) {
    throwIfAborted(signal);

    const params = {
      ...requestParams,
      maxResultCount: LIST_PAGE_SIZE,
      skipCount: items.length,
      ...(assetType ? { assetType } : {}),
    };
    const { payload } = await getList(params, signal);
    throwIfAborted(signal);

    const pageItems = payload?.items;

    if (!Array.isArray(pageItems) || pageItems.length > LIST_PAGE_SIZE) {
      throwInvalidPagedListResponse();
    }

    const pageHasTotalCount =
      payload?.totalCount !== null && payload?.totalCount !== undefined;

    if (hasTotalCount === undefined) {
      hasTotalCount = pageHasTotalCount;
    } else if (hasTotalCount !== pageHasTotalCount) {
      throwInvalidPagedListResponse();
    }

    if (pageHasTotalCount) {
      const pageTotalCount = payload.totalCount;

      if (!Number.isInteger(pageTotalCount) || pageTotalCount < 0) {
        throwInvalidPagedListResponse();
      }

      if (totalCount === undefined) {
        totalCount = pageTotalCount;
      } else if (totalCount !== pageTotalCount) {
        throwInvalidPagedListResponse();
      }
    }

    for (const item of pageItems) {
      if (
        !item ||
        item.id === null ||
        item.id === undefined ||
        item.id === ''
      ) {
        throwInvalidPagedListResponse();
      }

      const itemId = String(item.id);
      if (seenIds.has(itemId)) {
        throwInvalidPagedListResponse();
      }

      seenIds.add(itemId);
    }

    if (
      totalCount !== undefined &&
      items.length + pageItems.length > totalCount
    ) {
      throwInvalidPagedListResponse();
    }

    items.push(...pageItems);

    if (totalCount !== undefined) {
      if (items.length === totalCount) {
        return items;
      }

      if (pageItems.length === 0) {
        throwInvalidPagedListResponse();
      }
    } else if (pageItems.length < LIST_PAGE_SIZE) {
      return items;
    }
  }
}

const getPageOrderStart = (pagesOrder) =>
  pagesOrder.reduce((highestOrder, pageOrder) => {
    const order = Number(pageOrder?.order);
    if (Number.isFinite(order) && order > highestOrder) {
      return order;
    }
    return highestOrder;
  }, -1);

const getMediaLibraryPageType = (mediaAsset) => {
  if (mediaAsset?.assetType === 'video' || mediaAsset?.assetType === 'image') {
    return mediaAsset.assetType;
  }

  if (
    typeof mediaAsset?.mimeType === 'string' &&
    mediaAsset.mimeType.toLowerCase().startsWith(VIDEO_MIME_PREFIX)
  ) {
    return 'video';
  }

  if (
    typeof mediaAsset?.url === 'string' &&
    VIDEO_URL_EXTENSIONS_REGEX.test(mediaAsset.url)
  ) {
    return 'video';
  }

  return 'image';
};

const toPositiveNumber = (value, fallbackValue) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallbackValue;
};

const isVideoElement = (elem) => elem?.type === 'video';

const getPageTelemetryPayload = (pages) => {
  const safePages = Array.isArray(pages) ? pages : [];
  const changedPages = safePages.filter((page) => page?.isChanged);

  return {
    pageCount: safePages.length,
    changedPageCount: changedPages.length,
    videoPageCount: safePages.filter((page) =>
      page?.elements?.some(isVideoElement)
    ).length,
    changedVideoPageCount: changedPages.filter((page) =>
      page?.elements?.some(isVideoElement)
    ).length,
  };
};

const getErrorTelemetryPayload = (error) => ({
  errorName: error?.name || 'Error',
  ...(error?.response?.status ? { responseStatus: error.response.status } : {}),
});

const backendAssetTypesByValue = {
  0: AssetTypes.Story,
  1: AssetTypes.StoryThumbnail,
  2: AssetTypes.StoryIcon,
  3: AssetTypes.Landscape,
  5: AssetTypes.Clip,
  6: AssetTypes.ClipThumbnail,
  7: AssetTypes.Print,
  8: AssetTypes.Square,
  9: AssetTypes.Portrait,
  10: AssetTypes.Vertical,
  11: AssetTypes.Ad,
  15: AssetTypes.Wide,
  16: AssetTypes.ThreeFour,
};

const templateAssetTypePriorityByType = Object.entries(
  backendAssetTypesByValue
).reduce(
  (priorities, [backendValue, assetType]) => ({
    ...priorities,
    [assetType]: Number(backendValue),
  }),
  {}
);

const normalizedAssetTypesByValue = Object.values(AssetTypes).reduce(
  (assetTypes, assetType) => ({
    ...assetTypes,
    [assetType.toLowerCase()]: assetType,
  }),
  {}
);

const normalizeTemplateAssetType = (assetType) => {
  if (assetType === null || assetType === undefined) {
    return null;
  }

  if (
    Object.prototype.hasOwnProperty.call(backendAssetTypesByValue, assetType)
  ) {
    return backendAssetTypesByValue[assetType];
  }

  const normalizedAssetType = String(assetType).trim().toLowerCase();

  return normalizedAssetTypesByValue[normalizedAssetType] || null;
};

const normalizeTemplateAssetTypes = (assetTypes) => {
  if (!Array.isArray(assetTypes)) {
    return [];
  }

  const normalizedAssetTypes = [
    ...new Set(assetTypes.map(normalizeTemplateAssetType).filter(Boolean)),
  ];

  // The backend relationship does not expose an ordering contract. Mirror the
  // StudioAssetType enum order so the effective template type is stable.
  return normalizedAssetTypes.sort((leftAssetType, rightAssetType) => {
    const leftPriority =
      templateAssetTypePriorityByType[leftAssetType] ?? Number.MAX_SAFE_INTEGER;
    const rightPriority =
      templateAssetTypePriorityByType[rightAssetType] ??
      Number.MAX_SAFE_INTEGER;

    return (
      leftPriority - rightPriority ||
      leftAssetType.localeCompare(rightAssetType)
    );
  });
};

const mapTemplateListItemToCanvasPage = (template) => {
  const page = JSON.parse(template.definition);

  return {
    ...pageToCanvasPage(page),
    templateId: template.id,
    id: template.id,
    posterUrl: template.posterUrl,
    title: template.title,
    folder: template.folder,
    folderId: template.folderId,
    folderPath: template.folderPath,
    concurrencyStamp: template.concurrencyStamp,
    displayOrder: template.displayOrder,
    isPinned: Boolean(template.isPinned),
  };
};

const mapMediaLibraryAssetsToStudioPages = ({
  mediaLibraryAssets,
  clipOnly = false,
}) => {
  if (!Array.isArray(mediaLibraryAssets) || mediaLibraryAssets.length === 0) {
    return { pagesToImport: [], pagesOrder: [] };
  }

  const normalizedAssets = mediaLibraryAssets.filter(
    (asset) => asset && typeof asset.url === 'string' && asset.url
  );

  if (!normalizedAssets.length) {
    return { pagesToImport: [], pagesOrder: [] };
  }

  const assetsToImport = clipOnly
    ? normalizedAssets.slice(0, 1)
    : normalizedAssets;

  const pagesToImport = assetsToImport.map((asset, index) => {
    const assetType = getMediaLibraryPageType(asset);
    const isVideo = assetType === 'video';
    const pageId = uuidv4();

    return {
      id: pageId,
      assetType,
      type: isVideo ? PageType.Video : PageType.Image,
      url: asset.url,
      playcardUrl: asset.playcardUrl || asset.url,
      isProcessing: false,
      sortOrder: index,
      duration: 0,
      width: toPositiveNumber(asset.width, DEFAULT_MEDIA_WIDTH),
      height: toPositiveNumber(asset.height, DEFAULT_MEDIA_HEIGHT),
      mimeType: asset.mimeType || (isVideo ? 'video/mp4' : 'image/jpeg'),
      engagementUnitTitle: '',
      questionId: null,
    };
  });

  const pagesOrder = pagesToImport.map((page, index) => ({
    id: page.id,
    studioPageId: null,
    order: index,
    engagementUnitTitle: '',
  }));

  return { pagesToImport, pagesOrder };
};

const mergeMediaLibraryPagesToImport = ({
  existingPagesToImport,
  existingPagesOrder,
  mediaLibraryAssets,
  clipOnly = false,
}) => {
  const currentPagesToImport = Array.isArray(existingPagesToImport)
    ? existingPagesToImport
    : [];
  const currentPagesOrder = Array.isArray(existingPagesOrder)
    ? existingPagesOrder
    : [];

  const { pagesToImport: mediaPagesToImport, pagesOrder: mediaPagesOrder } =
    mapMediaLibraryAssetsToStudioPages({
      mediaLibraryAssets,
      clipOnly,
    });

  if (!mediaPagesToImport.length) {
    return {
      pagesToImport: currentPagesToImport,
      pagesOrder: currentPagesOrder,
    };
  }

  if (clipOnly) {
    return {
      pagesToImport: mediaPagesToImport,
      pagesOrder: mediaPagesOrder,
    };
  }

  const pageOrderStart = getPageOrderStart(currentPagesOrder) + 1;
  const appendedPagesOrder = mediaPagesOrder.map((pageOrder, index) => ({
    ...pageOrder,
    order: pageOrderStart + index,
  }));

  return {
    pagesToImport: [...currentPagesToImport, ...mediaPagesToImport],
    pagesOrder: [...currentPagesOrder, ...appendedPagesOrder],
  };
};

function createApiProvider({
  animatedThumbnail,
  authContext,
  studioContext,
  cmsApi,
  hasUploadsFeature = false,
  uploadSession,
}) {
  if (!studioContext || !cmsApi) return null;

  const {
    assetType,
    contentBankAssetType,
    cardsAssetType,
    resourceId,
    studioDefinitionId,
    storyId,
    studioStoryId,
    mediaLibraryAssets,
    setTemplateAssetTypes,
  } = studioContext;
  const {
    studioStoryApi,
    studioStoryThumbnailApi,
    studioStoryIconApi,
    studioContentBankApi,
    studioCardsAssetApi,
    studioTemplateApi,
    templateApi,
    templateFolderApi,
    mediaApi,
    uploadsApi,
    mediaProviderApi,
    mediaSourceApi,
    mediaCollectionsApi,
    externalAssetsApi,
    fontsApi,
    shapesApi,
    stickersApi,
    textSetsApi,
    studioClipApi,
    studioClipThumbnailApi,
    studioAdApi,
    studioSuggestionsApi,
    studioSavedColorsApi,
    studioAnimationApi,
    studioLutFiltersApi,
  } = cmsApi;

  const uploadSource = getUploadSource(studioContext);
  const isAnimatedThumbnail = isAnimatedThumbnailAssetType(assetType);
  // Kept here as well as in context: context updates do not rebuild this
  // provider, so its callbacks cannot read them.
  let loadedTemplateAssetTypes = [];

  const trackPageProcessing = async ({
    entityId,
    entityType,
    targetElement,
  }) => {
    if (process.env.NODE_ENV !== 'production') {
      console.log(
        'Studio: Asset Processing Started',
        entityId,
        entityType,
        targetElement
      );
    }

    await trackAppInsightsEvent(
      'Studio: Asset Processing Started',
      {
        entityId,
        entityType,
        targetElement,
      },
      true
    );
  };

  const createDefaultStory = () => ({
    ...getInitStory,
  });

  const saveStoryById = (story) => {
    return Promise.resolve({
      status: 'draft',
      slug: '',
      link: '',
      preview_link: {},
      edit_link: {},
      embed_post_link: {},
      featured_media: {},
    });
  };

  const getPageDuration = async (currentPage, pageIndex) => {
    let pageDuration = 0;
    let isVideoPage = false;

    for (
      let videoIndex = 0;
      videoIndex < currentPage.elements.length;
      videoIndex++
    ) {
      const elem = currentPage.elements[videoIndex];

      if (elem.type === 'video') {
        isVideoPage = true;

        const trimRange = getVideoTrimRange(elem.resource);
        if (trimRange) {
          pageDuration = Math.max(pageDuration, trimRange.lengthSeconds);
          continue;
        }

        const startedAt = Date.now();
        trackAppInsightsEvent('Studio: Video Metadata Probe Started', {
          pageIndex,
          videoIndex,
        });

        try {
          var { originalLength: length } = await getVideoLength(
            elem.resource.src
          );

          trackAppInsightsEvent('Studio: Video Metadata Probe Completed', {
            pageIndex,
            videoIndex,
            durationMs: Date.now() - startedAt,
            videoDurationSeconds: length,
          });

          if (length > pageDuration) {
            pageDuration = length;
          }
        } catch (e) {
          trackAppInsightsEvent('Studio: Video Metadata Probe Failed', {
            pageIndex,
            videoIndex,
            durationMs: Date.now() - startedAt,
            ...getErrorTelemetryPayload(e),
          });

          throw e;
        }
      }
    }

    if (!isVideoPage && currentPage.cssAnimations?.length > 0) {
      const animationsDuration = currentPage.cssAnimations.reduce(
        (maxAnimationDuration, cssAnimation) => {
          if (isNaN(cssAnimation.animationDuration)) {
            return maxAnimationDuration;
          }

          const animationDuration =
            cssAnimation.animationDuration + cssAnimation.animationDelay;

          return animationDuration > maxAnimationDuration
            ? animationDuration
            : maxAnimationDuration;
        },
        0
      );

      return animationsDuration;
    }

    return pageDuration;
  };

  const preparePagesToSave = async (pages) => {
    const result = [];
    const telemetryPayload = getPageTelemetryPayload(pages);
    const startedAt = Date.now();

    trackAppInsightsEvent('Studio: Prepare Pages Started', telemetryPayload);

    try {
      for (let i = 0; i < pages.length; i++) {
        const page = pages[i];

        let duration = 0;
        if (page.isChanged) {
          duration = await getPageDuration(page, i);
        }

        let cmsPageType = page.cmsPageType;

        if (
          page.cmsPageType !== PageType.Poll &&
          page.cmsPageType !== PageType.TriviaQuiz
        ) {
          const isVideoPage = hasVideoRenderingElement(page);
          cmsPageType = isVideoPage ? PageType.Video : PageType.Image;
        }

        result.push({
          duration,
          ampId: page.id,
          animations: page.animations || [],
          cssAnimations: page.cssAnimations || [],
          cmsPageId: page.cmsPageId,
          cmsPageType,
          hidden: page.hidden,
          isChanged: page.isChanged,
          isProcessing: page.isProcessing,
          sortOrder: i,
        });
      }

      trackAppInsightsEvent('Studio: Prepare Pages Completed', {
        ...telemetryPayload,
        durationMs: Date.now() - startedAt,
      });

      return result;
    } catch (e) {
      trackAppInsightsEvent('Studio: Prepare Pages Failed', {
        ...telemetryPayload,
        durationMs: Date.now() - startedAt,
        ...getErrorTelemetryPayload(e),
      });

      throw e;
    }
  };

  const getStudioStoryDefinition = () => {
    if (isAnimatedThumbnail && animatedThumbnail?.source) {
      const pageId = resourceId;

      if (animatedThumbnail.studioDefinition) {
        const preparedDefinition = prepareAnimatedThumbnailStudioDefinition(
          animatedThumbnail.studioDefinition,
          animatedThumbnail.source
        );
        return Promise.resolve({
          payload: {
            definition: preparedDefinition.definition,
            title: animatedThumbnail.source.label,
            pagesToImport: [],
            pagesOrder: [
              {
                id: pageId,
                studioPageId: preparedDefinition.studioPageId || pageId,
                order: 0,
                engagementUnitTitle: '',
              },
            ],
          },
        });
      }

      return Promise.resolve({
        payload: {
          definition: null,
          title: animatedThumbnail.source.label,
          pagesToImport: [
            {
              id: pageId,
              assetType: 'video',
              type: PageType.Video,
              url: animatedThumbnail.source.url,
              playcardUrl: animatedThumbnail.source.posterUrl || undefined,
              isProcessing: false,
              sortOrder: 0,
              duration: animatedThumbnail.getDuration?.() || 0,
              width: animatedThumbnail.source.width || DEFAULT_MEDIA_WIDTH,
              height: animatedThumbnail.source.height || 1620,
              mimeType: 'video/mp4',
              engagementUnitTitle: '',
              questionId: null,
            },
          ],
          pagesOrder: [
            {
              id: pageId,
              studioPageId: null,
              order: 0,
              engagementUnitTitle: '',
            },
          ],
        },
      });
    } else if (assetType === AssetTypes.Story && storyId) {
      return studioStoryApi.get(studioStoryId, storyId);
    } else if (assetType === AssetTypes.StoryThumbnail && resourceId) {
      return studioStoryThumbnailApi.get(studioDefinitionId, resourceId);
    } else if (assetType === AssetTypes.StoryIcon && resourceId) {
      return studioStoryIconApi.get(studioDefinitionId, resourceId);
    } else if (assetType === AssetTypes.ContentBankAsset && resourceId) {
      return studioContentBankApi.get(studioDefinitionId, resourceId);
    } else if (assetType === AssetTypes.CardsAsset && resourceId) {
      return studioCardsAssetApi.get(studioDefinitionId, resourceId);
    } else if (assetType === AssetTypes.Clip && resourceId) {
      return studioClipApi.get(studioDefinitionId, resourceId);
    } else if (assetType === AssetTypes.ClipThumbnail && resourceId) {
      return studioClipThumbnailApi.get(studioDefinitionId, resourceId);
    } else if (assetType === AssetTypes.Ad && resourceId) {
      return studioAdApi.get(studioDefinitionId, resourceId);
    } else if (assetType === AssetTypes.Template && resourceId) {
      return studioTemplateApi.get(studioDefinitionId, resourceId);
    }
  };

  const savePagesWithoutCapture = async ({
    storyToSave,
    story,
    deletedPagesIds,
    addNewPagesAtTheEnd,
    studioUploads,
  }) => {
    if (isAnimatedThumbnail) {
      if (
        animatedThumbnail?.getSaveDisabledReason?.() ||
        typeof animatedThumbnail?.generate !== 'function'
      ) {
        throw new Error(
          animatedThumbnail?.getSaveDisabledReason?.() ||
            'Animated thumbnail generation is unavailable.'
        );
      }

      const pages = (storyToSave?.pages || []).filter(
        (page) => !page.isProcessing
      );
      if (pages.length !== 1) {
        throw new Error(
          'Animated thumbnails must contain exactly one editable page.'
        );
      }

      const [page] = pages;

      const sourceElements = findAnimatedThumbnailSourceElements(
        page,
        animatedThumbnail.source
      );
      if (sourceElements.length === 0) {
        throw new Error(
          'The animated-thumbnail source video is missing from the page.'
        );
      }

      const startSeconds = animatedThumbnail.getStartSeconds?.() || 0;
      const sourceElementSet = new Set(sourceElements);
      const compositionPage = {
        ...page,
        backgroundAudio: null,
        cmsPageType: PageType.Video,
        elements: (page.elements || []).map((element) => {
          if (!sourceElementSet.has(element)) {
            return element;
          }

          const sourceResource = {
            ...element.resource,
            ...(animatedThumbnail.source.sourceId
              ? { id: animatedThumbnail.source.sourceId }
              : {}),
            ...(animatedThumbnail.source.url
              ? { src: animatedThumbnail.source.url }
              : {}),
            isMuted: true,
            length: ANIMATED_THUMBNAIL_DURATION_SECONDS,
            lengthFormatted: '00:03',
            noAudio: true,
            sourceOffsetSeconds: startSeconds,
          };
          delete sourceResource.sourceEndSeconds;
          delete sourceResource.trimData;

          return { ...element, resource: sourceResource };
        }),
        id: resourceId,
        isChanged: true,
      };
      const compositionToSave = {
        ...storyToSave,
        backgroundAudio: undefined,
        canvasWidth: PAGE_WIDTH,
        pages: [compositionPage],
      };

      await animatedThumbnail.generate({
        ...(studioUploads?.length ? { studioUploads } : {}),
        studioDefinition: JSON.stringify(compositionToSave),
        page: {
          ampId: resourceId,
          animations: compositionPage.animations || [],
          cssAnimations: compositionPage.cssAnimations || [],
          cmsPageType: PageType.Video,
          duration: ANIMATED_THUMBNAIL_DURATION_SECONDS,
          hidden: compositionPage.hidden,
          isChanged: true,
          isProcessing: false,
          sortOrder: 0,
        },
      });
      return { storyChangedByOtherUser: false };
    }

    const { pages } = storyToSave;
    const { loadedPages, processingPages } = story;

    for (let page of pages) {
      page.isChanged = false; // isChanged flag is saved with StudioStory definition
      let originalPage = (loadedPages || []).find((x) => x.id === page.id);

      if (
        assetType === AssetTypes.ContentBankAsset ||
        assetType === AssetTypes.CardsAsset ||
        assetType === AssetTypes.Clip ||
        assetType === AssetTypes.ClipThumbnail ||
        assetType === AssetTypes.Ad ||
        assetType === AssetTypes.Template
      ) {
        page.id = studioDefinitionId;
      }

      const pageWasChanged = wasPageChanged(originalPage, page);

      if (pageWasChanged) {
        page.isChanged = true;
      }
    }

    const saveTelemetryPayload = getPageTelemetryPayload(pages);
    const resultPages = await preparePagesToSave(pages);

    storyToSave.pages = storyToSave.pages.filter((x) => !x.isProcessing);
    storyToSave.canvasWidth = PAGE_WIDTH;

    const basePayload = {
      ...(studioUploads?.length ? { studioUploads } : {}),
      definition: JSON.stringify(storyToSave),
      pages: resultPages,
    };

    if (assetType === AssetTypes.Story && storyId) {
      var payload = {
        ...basePayload,
        studioStoryId,
        storyId,
        processingPagesIds: processingPages,
        deletedPagesIds,
        addNewPagesAtTheEnd,
      };

      try {
        trackAppInsightsEvent('Studio: Generate Pages Request Prepared', {
          ...saveTelemetryPayload,
          requestBytes: new Blob([JSON.stringify(payload)]).size,
          definitionBytes: new Blob([basePayload.definition]).size,
        });
        const result = await studioStoryApi.generatePages(payload);

        // Wait for all tracking events to complete
        await Promise.all(
          payload.pages
            .filter((page) => page.isChanged)
            .map((page) => {
              return trackPageProcessing({
                entityId: page.cmsPageId,
                entityType: EntityType.Page,
                targetElement: EventTargetElement.StoryPage,
              });
            })
        );

        const firstPageWasChanged =
          payload.pages.find((page) => page.sortOrder === 0)?.isChanged ||
          false;
        const previousFirstPage = loadedPages?.[0];
        const firstPageWasDeleted =
          payload?.deletedPagesIds?.some(
            ({ ampId }) => ampId === previousFirstPage?.id
          ) || false;

        if (firstPageWasChanged || firstPageWasDeleted) {
          await trackPageProcessing({
            entityId: storyId,
            entityType: EntityType.Story,
            targetElement: EventTargetElement.StoryThumbnail,
          });

          await trackPageProcessing({
            entityId: storyId,
            entityType: EntityType.Story,
            targetElement: EventTargetElement.StoryIcon,
          });
        }

        return result.data;
      } catch (e) {
        trackAppInsightsError('Studio: Generate Pages Failed', e, {
          ...saveTelemetryPayload,
          deletedPageCount: deletedPagesIds?.length || 0,
          addNewPagesAtTheEnd: Boolean(addNewPagesAtTheEnd),
        });
        throw e;
      }
    }
    if (assetType === AssetTypes.StoryThumbnail && resourceId) {
      await studioStoryThumbnailApi
        .render({
          ...basePayload,
          studioStoryThumbnailId: studioDefinitionId,
          storyId: resourceId,
        })
        .then(async () => {
          await trackPageProcessing({
            entityId: storyId,
            entityType: EntityType.Story,
            targetElement: EventTargetElement.StoryThumbnail,
          });
        });
    }
    if (assetType === AssetTypes.StoryIcon && resourceId) {
      await studioStoryIconApi
        .render({
          ...basePayload,
          studioStoryIconId: studioDefinitionId,
          storyId: resourceId,
        })
        .then(async () => {
          await trackPageProcessing({
            entityId: resourceId,
            entityType: EntityType.Story,
            targetElement: EventTargetElement.StoryIcon,
          });
        });
    }

    if (assetType === AssetTypes.ContentBankAsset && resourceId) {
      await studioContentBankApi
        .render({
          ...basePayload,
          contentBankAssetId: resourceId,
          contentBankAssetDefinitionId: studioDefinitionId,
        })
        .then(async () => {
          await trackPageProcessing({
            entityId: resourceId,
            entityType: EntityType.ContentBankAsset,
            targetElement: EventTargetElement.ContentBankAsset,
          });
        });
    }

    if (assetType === AssetTypes.CardsAsset && resourceId) {
      await studioCardsAssetApi
        .render({
          ...basePayload,
          cardId: resourceId,
          studioCardId: studioDefinitionId,
        })
        .then(async () => {
          await trackPageProcessing({
            entityId: resourceId,
            entityType: EntityType.CardsAsset,
            targetElement: EventTargetElement.CardsAsset,
          });
        });
    }

    if (assetType === AssetTypes.Clip && resourceId) {
      const renderStartedAt = Date.now();
      const renderTelemetryPayload = {
        ...saveTelemetryPayload,
        changedPreparedPageCount: resultPages.filter((page) => page.isChanged)
          .length,
      };

      trackAppInsightsEvent(
        'Studio: Clip Render Started',
        renderTelemetryPayload
      );

      try {
        await studioClipApi.render({
          ...basePayload,
          clipId: resourceId,
          studioClipId: studioDefinitionId,
        });

        trackAppInsightsEvent('Studio: Clip Render Completed', {
          ...renderTelemetryPayload,
          durationMs: Date.now() - renderStartedAt,
        });

        await trackPageProcessing({
          entityId: resourceId,
          entityType: EntityType.Clip,
          targetElement: EventTargetElement.ClipAsset,
        });
      } catch (e) {
        trackAppInsightsEvent('Studio: Clip Render Failed', {
          ...renderTelemetryPayload,
          durationMs: Date.now() - renderStartedAt,
          ...getErrorTelemetryPayload(e),
        });

        throw e;
      }
    }

    if (assetType === AssetTypes.ClipThumbnail && resourceId) {
      await studioClipThumbnailApi
        .render({
          ...basePayload,
          clipId: resourceId,
          studioClipThumbnailId: studioDefinitionId,
        })
        .then(async () => {
          await trackPageProcessing({
            entityId: resourceId,
            entityType: EntityType.Clip,
            targetElement: EventTargetElement.ClipThumbnail,
          });
        });
    }

    if (assetType === AssetTypes.Ad && resourceId) {
      await studioAdApi
        .render({
          ...basePayload,
          interstitialAdId: resourceId,
          studioAdId: studioDefinitionId,
        })
        .then(async () => {
          await trackPageProcessing({
            entityId: resourceId,
            entityType: EntityType.InterstitialAd,
            targetElement: EventTargetElement.InterstitialAd,
          });
        });
    }

    if (assetType === AssetTypes.Template && resourceId) {
      const pageToSave = {
        ...storyToSave.pages[0],
        canvasWidth: PAGE_WIDTH,
      };
      await studioTemplateApi
        .save({
          jsonDefinition: JSON.stringify(pageToSave),
          ampHtml: storyToSave.content,
          templateId: resourceId,
        })
        .then(async () => {
          await trackPageProcessing({
            entityId: resourceId,
            entityType: EntityType.Template,
            targetElement: EventTargetElement.Template,
          });
        });
    }

    return { storyChangedByOtherUser: false };
  };

  const getStoryById = () => {
    return getStudioStoryDefinition().then((res) => {
      const studioPage = res.payload;
      if (assetType === AssetTypes.Template) {
        loadedTemplateAssetTypes = normalizeTemplateAssetTypes(
          studioPage.assetTypes
        );

        if (typeof setTemplateAssetTypes === 'function') {
          setTemplateAssetTypes(loadedTemplateAssetTypes);
        }
      }

      const { pagesToImport, pagesOrder } = studioPage;
      const normalizedPagesToImport = Array.isArray(pagesToImport)
        ? pagesToImport
        : [];
      const normalizedPagesOrder = Array.isArray(pagesOrder) ? pagesOrder : [];
      const shouldBootstrapFromMediaLibrary =
        Array.isArray(mediaLibraryAssets) && mediaLibraryAssets.length > 0;

      const mergedPages = shouldBootstrapFromMediaLibrary
        ? mergeMediaLibraryPagesToImport({
            existingPagesToImport: normalizedPagesToImport,
            existingPagesOrder: normalizedPagesOrder,
            mediaLibraryAssets,
            clipOnly: assetType === AssetTypes.Clip,
          })
        : {
            pagesToImport: normalizedPagesToImport,
            pagesOrder: normalizedPagesOrder,
          };

      if (shouldBootstrapFromMediaLibrary) {
        clearPersistedMediaLibraryAssets();
      }

      if (!studioPage.definition) {
        return {
          ...createDefaultStory(),
          pagesToImport: mergedPages.pagesToImport,
          pagesOrder: mergedPages.pagesOrder,
          title: { raw: studioPage.title },
        };
      }

      const storyData = JSON.parse(studioPage.definition);
      const { slug, date, modified, featuredMedia } = storyData;

      const pages =
        storyData.pages?.map((page) => {
          const pageOrder = normalizedPagesOrder.find(
            (x) =>
              page.cmsPageId === x.id ||
              (x.studioPageId != null && x.studioPageId === page.id)
          );

          const engagementUnitProps = pageOrder
            ? { engagementUnitTitle: pageOrder.engagementUnitTitle }
            : {};

          return {
            ...pageToCanvasPage(page),
            ...engagementUnitProps,
            cmsPageId: page.cmsPageId || pageOrder?.id,
            hidden: page.hidden ?? false,
          };
        }) || [];

      return {
        ...getInitStory,
        story_data: { pages, version: 35 },
        slug,
        date,
        modified,
        featured_media: featuredMedia,
        title: { raw: studioPage.title },
        excerpt: { raw: '' },
        pagesToImport: mergedPages.pagesToImport,
        pagesOrder: mergedPages.pagesOrder,
      };
    });
  };

  const addPageTemplate = (template) => {
    const { storyMarkup, ...templateToSave } = template;

    return templateApi
      .save({
        definition: JSON.stringify(templateToSave),
        ampPageDefinition: storyMarkup,
      })
      .then(({ payload: templateId }) => ({
        ...templateToSave,
        templateId,
      }));
  };

  const getEffectiveTemplateAssetType = (requestedAssetType) => {
    if (requestedAssetType) {
      return requestedAssetType;
    }

    if (assetType === AssetTypes.CardsAsset) {
      return cardsAssetType;
    }

    if (assetType === AssetTypes.ContentBankAsset) {
      return contentBankAssetType;
    }

    return cardsAssetType || contentBankAssetType || assetType;
  };

  const getCustomPageTemplates = (
    nextTemplatesToFetch,
    pageSize = 16,
    folderName = '',
    searchText = '',
    signal,
    assetType,
    folderId = null,
    includeDescendants = false
  ) => {
    return templateApi
      .getList(
        nextTemplatesToFetch,
        pageSize,
        folderName,
        searchText,
        signal,
        assetType,
        folderId,
        includeDescendants
      )
      .then(({ payload: resultPage }) => {
        const templates = resultPage.list.map(mapTemplateListItemToCanvasPage);

        return { templates, hasMore: resultPage.hasMore };
      });
  };

  /**
   * @param {number} pageNumber - page number to fetch
   * @param {number} pageSize
   * @param {string} searchText
   * @returns {*}
   */
  const getTemplatesFolders = (
    pageNumber,
    pageSize = 16,
    searchText = '',
    requestedAssetType
  ) => {
    const assetTypeToUse =
      requestedAssetType === null
        ? null
        : getEffectiveTemplateAssetType(requestedAssetType);

    return templateApi
      .getFolders(pageNumber, pageSize, searchText, assetTypeToUse)
      .then(({ payload: folders }) => {
        return folders;
      });
  };

  const getStudioTemplateFolders = async (
    {
      parentFolderId = null,
      searchText = '',
      assetType: requestedAssetType,
    } = {},
    signal
  ) => {
    const assetTypeToUse =
      requestedAssetType === null
        ? null
        : getEffectiveTemplateAssetType(requestedAssetType);
    const folders = await getDataFromList(templateFolderApi.getList, {
      assetType: assetTypeToUse,
      signal,
      requestParams: {
        ...(parentFolderId ? { parentFolderId } : {}),
        forStudio: true,
        ...(searchText ? { searchText } : {}),
      },
    });

    return { folders };
  };

  const getStudioTemplateFolder = async (folderId, signal) => {
    const { payload } = await templateFolderApi.get(folderId, signal);
    return payload;
  };

  const getStudioTemplateFolderDestinations = async (signal) => {
    const folders = await getDataFromList(templateFolderApi.getDestinations, {
      signal,
    });

    return { folders };
  };

  const movePageTemplate = (templateId, input = {}) =>
    templateApi
      .moveStudioTemplate(templateId, {
        destinationFolderId: input.destinationFolderId,
        concurrencyStamp: input.concurrencyStamp,
        assetType: getEffectiveTemplateAssetType(input.assetType),
      })
      .then(({ payload: template }) =>
        mapTemplateListItemToCanvasPage(template)
      );

  const updatePageTemplateMetadata = (templateId, input = {}) => {
    const request = {
      title: input.title,
      assetType: getEffectiveTemplateAssetType(input.assetType),
    };

    if (Object.prototype.hasOwnProperty.call(input, 'folderName')) {
      request.folderName = input.folderName;
    }

    return templateApi
      .updateStudioMetadata(templateId, request)
      .then(({ payload: template }) =>
        mapTemplateListItemToCanvasPage(template)
      );
  };

  const updatePageTemplateOrder = (templateId, input = {}) => {
    return templateApi
      .updateStudioOrder(templateId, {
        displayOrder: input.displayOrder,
        assetType: getEffectiveTemplateAssetType(input.assetType),
      })
      .then(({ payload: template }) =>
        mapTemplateListItemToCanvasPage(template)
      );
  };

  const updatePageTemplatePin = (templateId, input = {}) => {
    return templateApi
      .updateStudioPin(templateId, {
        isPinned: input.isPinned,
        assetType: getEffectiveTemplateAssetType(input.assetType),
      })
      .then(({ payload: template }) =>
        mapTemplateListItemToCanvasPage(template)
      );
  };

  const generateSuggestions = async (textElements) => {
    const assetId =
      assetType === AssetTypes.Story && storyId ? storyId : resourceId;

    // The CMS binds this to StudioAssetType, which has no template value, so a
    // template sends its own asset type (ST-5411).
    const assetTypeToUse =
      assetType === AssetTypes.ContentBankAsset
        ? contentBankAssetType
        : assetType === AssetTypes.CardsAsset
        ? cardsAssetType
        : assetType === AssetTypes.Template
        ? loadedTemplateAssetTypes[0] || AssetTypes.Story
        : assetType;

    const payload = {
      assetId,
      assetType: assetTypeToUse,
      textElements,
    };

    const result = await studioSuggestionsApi
      .generateSuggestions(payload)
      .catch((error) => {
        console.error(
          typeof error === 'string' ? new Error(error) : error,
          payload
        );
      });

    return { suggestions: result?.payload ?? [] };
  };

  const togglePageHidden = ({ pageId, isHidden }) => {
    return studioStoryApi.togglePageHidden(pageId).then(() => {
      return { pageId, isHidden };
    });
  };

  const getSavedColors = async ({ signal } = {}) => {
    const colors = await getDataFromList(studioSavedColorsApi.getList, {
      signal,
    });

    return { colors };
  };

  const getStudioAnimations = async ({
    searchTerm = '',
    pageToken = 1,
    pageSize = 24,
    signal,
  } = {}) => {
    const normalizedPageToken = Math.max(Number(pageToken) || 1, 1);
    const normalizedPageSize = Math.max(Number(pageSize) || 24, 1);
    const searchText = typeof searchTerm === 'string' ? searchTerm.trim() : '';
    const params = {
      includeHidden: false,
      maxResultCount: normalizedPageSize,
      skipCount: (normalizedPageToken - 1) * normalizedPageSize,
      ...(searchText ? { searchText } : {}),
    };

    const { payload } = await studioAnimationApi.getList(params, signal);
    const animations = payload?.items || [];
    const totalItems = Number(payload?.totalCount) || animations.length;
    const hasMore = params.skipCount + animations.length < totalItems;

    return {
      animations,
      hasMore,
      nextPageToken: hasMore ? normalizedPageToken + 1 : undefined,
      totalItems,
    };
  };

  const getStudioAnimation = async (animationId) => {
    const { payload } = await studioAnimationApi.get(animationId);

    return payload;
  };

  const getStudioLutFilters = async ({ signal } = {}) => {
    const filters = await getDataFromList(studioLutFiltersApi.getList, {
      signal,
    });

    return { filters };
  };

  const resolveStudioLutVersions = async (versionIds, { signal } = {}) => {
    const normalizedVersionIds = [
      ...new Set(
        (Array.isArray(versionIds) ? versionIds : [])
          .filter(Boolean)
          .map((versionId) => String(versionId).toLowerCase())
      ),
    ];

    if (normalizedVersionIds.length === 0) {
      return { assets: [] };
    }

    const { payload } = await studioLutFiltersApi.resolveVersions(
      normalizedVersionIds,
      signal
    );

    return { assets: Array.isArray(payload) ? payload : [] };
  };

  const addSavedColor = (color) => {
    return studioSavedColorsApi.addSavedColor(color).then(({ payload }) => {
      return { color: payload };
    });
  };

  const editSavedColor = (color) => {
    return studioSavedColorsApi.editSavedColor(color).then(({ payload }) => {
      return { color: payload };
    });
  };

  // const deleteSavedColor = (color) => {
  //   return studioSavedColorsApi.deleteSavedColor(color).then(({ payload }) => {
  //     return { color: payload };
  //   });
  // };

  const getMediaProviderCollections = ({
    mediaProviderId,
    searchTerm = '',
    folder,
  }) => {
    return mediaCollectionsApi
      .getList(mediaProviderId, searchTerm, folder)
      .then(({ payload }) => {
        return { tags: payload, tagsSearchTerm: searchTerm };
      });
  };

  const deletePageTemplate = (templateId, requestedAssetType) =>
    templateApi.deleteStudioTemplate(
      templateId,
      getEffectiveTemplateAssetType(requestedAssetType)
    );

  const savePages = async (request) => {
    const snapshot = uploadSession?.snapshot(request.storyToSave?.pages);
    const studioUploads =
      uploadSource && hasUploadsFeature
        ? snapshot?.uploads.map(({ resource, ampId }) => ({
            assetUrl: resource.src,
            mimeType: resource.mimeType,
            title: resource.title,
            ...(uploadSource.assetType === AssetTypes.Story ? { ampId } : {}),
          }))
        : undefined;
    const result = await savePagesWithoutCapture({ ...request, studioUploads });
    if (snapshot && !result?.storyChangedByOtherUser)
      uploadSession.saved(snapshot);
    return result;
  };

  const uploadPoster = async (
    correlationId,
    file,
    metadata,
    updateProgress
  ) => {
    const uploadedAssetUrl = await uploadsApi.uploadFileToR2(
      correlationId,
      '',
      file,
      updateProgress
    );

    if (!uploadedAssetUrl) {
      throw new Error('Asset upload failed. Uploaded asset URL is empty.');
    }

    let assetSize = await getImageDimensions(uploadedAssetUrl);
    const poster = {
      src: uploadedAssetUrl,
      width: assetSize.width,
      height: assetSize.height,
      mimeType: file.type,
      mediaId: metadata.post,
    };

    return { ...poster, includes: () => false };
  };

  const uploadMedia = async (
    correlationId,
    file,
    metadata,
    _media,
    updateProgress
  ) => {
    if (metadata.media_source === 'poster-generation') {
      return await uploadPoster(correlationId, file, metadata, updateProgress);
    }

    const isVideo = file.type.includes('video');
    const isAudio = file.type.includes('audio');
    const contentType = isVideo ? 'video' : isAudio ? 'audio' : 'image';

    // ST-5209: an incomplete video is never uploaded, so the editor's failure
    // path removes it from the canvas and media library, and the upload
    // session never sees it for capture.
    const incomplete = isVideo
      ? await getIncompleteVideoError(correlationId, file)
      : null;
    if (incomplete) {
      throw incomplete;
    }

    const uploadedAssetUrl = await uploadsApi.uploadFileToR2(
      correlationId,
      '',
      file,
      updateProgress
    );

    if (!uploadedAssetUrl) {
      throw new Error('Asset upload failed. Uploaded asset URL is empty.');
    }

    let assetSize = {};

    if (isVideo) {
      assetSize = await getVideoDimensions(uploadedAssetUrl);
    } else if (!isAudio) {
      assetSize = await getImageDimensions(uploadedAssetUrl);
    }

    const media = {
      alt: metadata.alt_text,
      src: uploadedAssetUrl,
      width: assetSize.width,
      height: assetSize.height,
      title: file.name,
      type: contentType,
      mimeType: file.type,
      sizes: [],
      local: true,
      isTranscoding: false,
      isMuting: false,
      isTrimming: false,
      id: uuidv4(),
    };

    if (metadata.original_id) {
      uploadSession?.derive(metadata.original_id, media.id);
    } else if (
      hasUploadsFeature &&
      uploadSource &&
      metadata.uploadOrigin === 'user-file' &&
      /^(image|video)\//.test(file.type)
    ) {
      uploadSession?.add(correlationId, media);
    }
    return { ...media };
  };

  const uploadMediaFromBase64String = async (
    correlationId,
    resource,
    updateProgress
  ) => {
    const uploadedAssetUrl = await uploadsApi.uploadBase64ToR2(
      correlationId,
      resource,
      updateProgress
    );

    if (!uploadedAssetUrl) {
      throw new Error('Asset upload failed. Uploaded asset URL is empty.');
    }

    let newResource = { ...resource };

    delete newResource.base64Str;
    newResource.src = uploadedAssetUrl;
    newResource.loading = false;

    const sizes = 'sizes' in newResource ? Object.keys(newResource.sizes) : [];

    sizes.forEach((size) => {
      newResource.sizes[size].source_url = uploadedAssetUrl;
    });

    return newResource;
  };

  // eslint-disable-next-line no-underscore-dangle
  const deleteMedia = (mediaId) => mediaApi._delete(mediaId);
  const deleteExternalAsset = (mediaId) => mediaSourceApi._delete(mediaId);

  const getMediaFromProvider = async (mediaProviderId, params) => {
    const { payload } = await mediaProviderApi.getMedias(
      mediaProviderId,
      params
    );
    const { media, nextPageToken } = payload;

    return {
      media,
      nextPageToken,
    };
  };

  const { registerUsage, download } = mediaProviderApi;

  const getExternalAssets = async (params, signal) => {
    const { payload } = await externalAssetsApi.getExternalAssets(
      params,
      signal
    );

    return payload;
  };

  const getFonts = async ({ signal } = {}) => {
    const items = await getDataFromList(fontsApi.getList, { signal });

    return items.map(({ fontMetrics }) => JSON.parse(fontMetrics));
  };

  const getShapes = async ({ signal } = {}) => {
    const items = await getDataFromList(shapesApi.getList, { signal });

    return items;
  };

  const getStickers = async ({ signal } = {}) => {
    const items = await getDataFromList(stickersApi.getList, { signal });

    return items;
  };

  const getTextSets = async ({ signal } = {}) => {
    const items = await getDataFromList(textSetsApi.getList, {
      assetType: contentBankAssetType || cardsAssetType || assetType,
      signal,
    });

    return items.map(({ definition, coverImageUrl }) => ({
      ...JSON.parse(definition),
      coverImageUrl,
      isCustom: true,
    }));
  };

  return {
    ...mockApiProvider,
    saveStoryById,
    autoSaveById: saveStoryById,
    getStoryById,
    addPageTemplate,
    getCustomPageTemplates,
    getTemplatesFolders,
    getStudioTemplateFolders,
    getStudioTemplateFolder,
    getStudioTemplateFolderDestinations,
    movePageTemplate,
    updatePageTemplateMetadata,
    updatePageTemplateOrder,
    updatePageTemplatePin,
    getMediaProviderCollections,
    generateSuggestions,
    deletePageTemplate,
    uploadMedia,
    uploadMediaFromBase64String,
    deleteMedia,
    deleteExternalAsset,
    savePages,
    studioStoryId,
    storyId,
    getMediaFromProvider,
    getMediaDetailsFromProvider: mediaProviderApi.getMediaDetails,
    registerUsage,
    download3pAsset: download,
    getExternalAssets,
    getFonts,
    getShapes,
    getStickers,
    getTextSets,
    getSavedColors,
    addSavedColor,
    editSavedColor,
    getStudioAnimations,
    getStudioAnimation,
    getStudioLutFilters,
    resolveStudioLutVersions,
    // deleteSavedColor,
    togglePageHidden,
  };
}

const useStudioApiProvider = ({
  animatedThumbnail,
  studioContext,
  story,
  hasUploadsFeature = false,
}) => {
  const cmsApi = useCmsApi();
  const { authContext } = useAuthorizationManager();
  const uploadSession = useUploadSession(studioContext, authContext);
  const hasStudioContext = Boolean(studioContext);
  const {
    assetType,
    contentBankAssetType,
    cardsAssetType,
    resourceId,
    studioDefinitionId,
    storyId,
    studioStoryId,
    mediaLibraryAssets,
    setTemplateAssetTypes,
  } = studioContext || {};
  const animatedThumbnailRef = useRef(animatedThumbnail);
  animatedThumbnailRef.current = animatedThumbnail;
  const { source } = animatedThumbnail || {};
  const hasAnimatedThumbnail = Boolean(animatedThumbnail);

  const animatedThumbnailProvider = useMemo(
    () =>
      hasAnimatedThumbnail
        ? {
            generate: (composition) =>
              animatedThumbnailRef.current.generate(composition),
            getStartSeconds: () => animatedThumbnailRef.current.startSeconds,
            getDuration: () => animatedThumbnailRef.current.duration,
            getSaveDisabledReason: () =>
              animatedThumbnailRef.current.saveDisabledReason,
            source,
            studioDefinition: animatedThumbnailRef.current.studioDefinition,
          }
        : null,
    [hasAnimatedThumbnail, source]
  );

  const provider = useMemo(
    () =>
      createApiProvider({
        animatedThumbnail: animatedThumbnailProvider,
        authContext,
        // Loading template metadata updates context without changing API inputs.
        // Keep callbacks stable so that update cannot restart editor loading.
        studioContext: hasStudioContext
          ? {
              assetType,
              contentBankAssetType,
              cardsAssetType,
              resourceId,
              studioDefinitionId,
              storyId,
              studioStoryId,
              mediaLibraryAssets,
              setTemplateAssetTypes,
            }
          : null,
        story,
        cmsApi,
        hasUploadsFeature,
        uploadSession,
      }),
    [
      animatedThumbnailProvider,
      authContext,
      hasStudioContext,
      assetType,
      contentBankAssetType,
      cardsAssetType,
      resourceId,
      studioDefinitionId,
      storyId,
      studioStoryId,
      mediaLibraryAssets,
      setTemplateAssetTypes,
      story,
      cmsApi,
      hasUploadsFeature,
      uploadSession,
    ]
  );

  return provider;
};

export default useStudioApiProvider;
export { createApiProvider };

/* eslint-enable import/no-duplicates, import/order, no-unused-vars */
